import {Injectable} from '@angular/core';

import 'rxjs/add/operator/map';
import {Observable} from 'rxjs/Rx';
import PouchDB from 'pouchdb';
import PouchFind from 'pouchdb-find';
import {from, forkJoin, combineLatest} from 'rxjs';
// Classes.
import {DataContainer} from '../db/data-container';
import {AthleteGeneralInfoItem} from '../fetching/types/athlete-general-info.item';

// Providers.
import {LoggerService, LogLevel} from '../logger/logger-service';
import {EnvironmentService} from '../wa-services/environment-service';
import {AthletesDatabaseAbstractService} from '../wa-services/athletes-database-abstract.service';

// Interfaces.
import {AthleteStruct, AthleteSearchResult} from '../interfaces/athlete-database-service.interface'



class AthleteContainerPouchDb {
  constructor(
    public _id: string,
    public _rev: string,
    public athlete: AthleteStruct
  ) {}
}


@Injectable()
export class AthletesDatabasePouchDbService extends AthletesDatabaseAbstractService {

  protected _db: any;


  constructor(public logger: LoggerService,
    protected env: EnvironmentService) {
    super(logger, env);
    this.setLogLevel(LogLevel.DEBUG);
    this.initDB();
    
  }

  initDB() {
    //Login for test and transpilation
    PouchDB.plugin(PouchFind);
    this._db = new PouchDB('wa_athletes', {
      auto_compaction: false,
      revs_limit: 1
    });
    this.prepareIndexes();
  }

  destroy() {
    this._db.destroy();
  }

  protected prepareIndexes() {
    forkJoin([
      from(this._db.createIndex({index: {fields: ['athlete.sCompleteName']}})),
      from(this._db.createIndex({index: {fields: ['athlete.sPartialName']}}))
    ]).subscribe(() => this._dbIsReady.next(true));
  }

  get(athId: number): Observable<AthleteStruct> {
    this.debug(`Trying to recover athlete  with id '${athId}'`);
    return from(this._db.get(athId + ''))
      .map((athleteContainer: AthleteContainerPouchDb) => athleteContainer.athlete);
  }

  protected processMiss(reason: any, dataContainer: DataContainer<any>) {
    if (reason.message && reason.message === 'missing') {
      // Miss, so this is a new insert.
      let id = dataContainer.getLocator().getId();
      this.debug(`Athlete id '${id}' not found, doing an insert`);
      this._db.put(dataContainer)
      // Errors come from a duplciate insertion, ignore them as last insetion
      // would have all data.
      .catch(() => false);
    }
    else {
      this.error('Error inserting athlete in database. Error received: ', reason);
    }
  }

  public storeList(list: AthleteGeneralInfoItem[]): Observable<any> {
    return this._db.bulkDocs(
      list.map(item => this.AthleteGeneralToAthleteContainer(item))
    );
  }

  public search(query: string): Observable<AthleteSearchResult> {

    let preparedQuery = AthletesDatabaseAbstractService.prepateStringForSearch(query);

    this.log(`Performing search for «${query}» as «${preparedQuery}»`);

    let selectorCondition = {$gte: preparedQuery, $lt: preparedQuery + "\ufff0"};

    let completeNameQueryObject = {
      selector: {
        "athlete.sCompleteName": selectorCondition,
      },
      limit: 50
    };

    let partialQueryObject = {
      selector: {
        "athlete.sPartialName": selectorCondition,
      },
      limit: 50
    };

    return combineLatest(
      from(this._db.find(completeNameQueryObject)),
      from(this._db.find(partialQueryObject)),
    )
      .map(c => this.mergeResults(c))
  }

  protected mergeResults(resultArrays): AthleteSearchResult {
    let result = new AthleteSearchResult([], false);
    let seen = {};

    // Put every result in the same bucket (to remove duplicates).
    resultArrays.forEach(item => {
      item.docs.forEach(doc => {
        if (typeof seen[doc._id] === 'undefined') {
          seen[doc._id] = doc.athlete;
          result.items.push(doc.athlete);
        }
      })
    });

    result.truncated = result.items.length > 50;
    result.items = result.items.slice(0, 50);

    return result;
  }

  public getInfo(): Observable<any> {
    return from(this._db.info());
  }

  public getVersion(): string {
    return PouchDB.version;
  }
}
